import { defineStore, acceptHMRUpdate } from "pinia";
import { useApiStore, type IAcceptInvitePayload } from "./api";
import { useNotificationStore } from "./notification";

export type Permission =
  | "area_tag/create"
  | "area_tag/delete"
  | "area_tag/edit"
  | "area_tag/view"
  | "chair/create"
  | "chair/delete"
  | "chair/edit"
  | "chair/view"
  | "chair/override_app_version"
  | "component/create"
  | "component/delete"
  | "component/edit"
  | "component/view"
  | "customer/create"
  | "customer/delete"
  | "customer/edit"
  | "customer/view"
  | "invite/create"
  | "invite/delete"
  | "invite/edit"
  | "invite/view"
  | "market/create"
  | "market/delete"
  | "market/edit"
  | "market/view"
  | "massage_program/create"
  | "massage_program/delete"
  | "massage_program/edit"
  | "massage_program/view"
  | "page/create"
  | "page/delete"
  | "page/edit"
  | "page/view"
  | "sector/create"
  | "sector/delete"
  | "sector/edit"
  | "sector/view"
  | "segment/create"
  | "segment/delete"
  | "segment/edit"
  | "segment/view"
  | "seller/create"
  | "seller/delete"
  | "seller/edit"
  | "seller/view"
  | "training_category/create"
  | "training_category/delete"
  | "training_category/edit"
  | "training_category/view"
  | "training_program/create"
  | "training_program/delete"
  | "training_program/edit"
  | "training_program/view"
  | "user/delete"
  | "user/edit"
  | "user/view";

export interface IAuthState {
  name: string;
  token: string;
  email: string;
  roleId: number | null;
  permissions: Permission[];
  error: string;
  api?: ReturnType<typeof useApiStore>;
  notification?: ReturnType<typeof useNotificationStore>;
}

export interface IAuthActions {
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  me(): Promise<void>;
  refresh(): void;
  acceptInvite(token: string, payload: IAcceptInvitePayload): Promise<void>;
  rejectInvite(token: string): Promise<void>;
  hasPermission(permission: Permission): boolean;
}

const TOKEN_NAME = "promas-token";

export const useAuthStore = defineStore<"auth", IAuthState, {}, IAuthActions>(
  "auth",
  {
    state: () => ({
      name: "",
      token: "",
      email: "",
      roleId: null,
      permissions: [
        "area_tag/view",
        "chair/view",
        // "component/view",
        "customer/view",
        // "market/view",
        "massage_program/view",
        // "page/view",
        "seller/view",
        "training_category/view",
        "training_program/view",
      ],
      error: "",
    }),

    getters: {
      api: () => useApiStore(),
      notification: () => useNotificationStore(),
    },

    actions: {
      /**
       * Attempt to login user
       * @param {string} email - email of user
       * @param {string} password - password of user
       */
      async login(email: string, password: string) {
        this.$reset();
        try {
          const { data } = await this.api.login(email, password);
          this.$patch({
            ...data.data,
          });
          /** Store token in localStorage */
          window.localStorage.setItem(TOKEN_NAME, data.data.token);
          /** Fetch user information */
          this.me();
        } catch (error) {
          this.$patch({ error: error.message });
        }
      },

      /**
       * Attempt to logout user
       */
      async logout() {
        try {
          await this.api.logout();
        } catch (error) {
          this.notification.notifyError(error.message);
        }

        this.$reset();
        window.localStorage.removeItem(TOKEN_NAME);
        this.router.push("/");
      },

      /**
       * Attempt to fetch user information
       */
      async me() {
        try {
          const { data } = await this.api.me();
          this.$patch({
            ...data.data.user,
            roleId: data.data.user.role_id,
          });
          /** Only redirect to '/home' if user is on root path */
          if (window.location.pathname === "/") {
            this.router.push("/home");
          }
        } catch (error) {
          this.logout();
        }
      },

      /**
       * Attempt to refresh on initial load
       */
      refresh() {
        const token = window.localStorage.getItem(TOKEN_NAME);
        this.$patch({ token });
        this.me();
      },

      /**
       * Accept invite
       */
      async acceptInvite(token: string, payload: IAcceptInvitePayload) {
        try {
          await this.api.acceptInvite(token, payload);
        } catch (error) {
          this.notification.notifyError(error.message);
        }
      },

      /**
       * Reject invite
       */
      async rejectInvite(token: string) {
        try {
          await this.api.deleteInvite(token);
        } catch (error) {
          this.notification.notifyError(error.message);
        }
      },

      /**
       * Check if user has permission
       * @param {Permission} permission - permission to check
       */
      hasPermission(permission: Permission) {
        return this.permissions.includes(permission);
      },
    },
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
